/**
 * Drive "Shared with me" side page.
 */

import { h } from 'preact'
import { useCallback, useEffect, useState } from 'preact/hooks'
import { initDriveLightbox } from '../../modules/drive/lightbox.js'
import {
  createDriveI18n,
  fetchJson,
  formatDate,
  jsonUrl,
  lightboxProps
} from './side-utils.js'
import { DriveSideChrome } from './DriveSideChrome.jsx'

export function SharedWithMePage() {
  const i18n = createDriveI18n()
  const t = (key) => i18n.t(key)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setData(await fetchJson(jsonUrl()))
    } catch (e) {
      setError(e.message)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!data) return undefined
    const root = document.getElementById('drive-side-preact-root')
    const lightbox = initDriveLightbox(root)
    return () => lightbox?.destroy?.()
  }, [data])

  if (error && !data) {
    return (
      <DriveSideChrome titleKey="drive.shared_with_me" titleIcon="bi-people-fill">
        <div class="alert alert-danger">{error}</div>
      </DriveSideChrome>
    )
  }
  if (!data) {
    return (
      <DriveSideChrome titleKey="drive.shared_with_me" titleIcon="bi-people-fill">
        <div class="text-muted py-4 text-center">…</div>
      </DriveSideChrome>
    )
  }

  const shares = data.shares || []

  return (
    <DriveSideChrome titleKey="drive.shared_with_me" titleIcon="bi-people-fill">
    <div class="drive-container">
      {error && <div class="alert alert-warning">{error}</div>}
      {shares.length === 0 ? (
        <div class="empty-state text-center py-5">
          <i class="bi bi-people fs-1 text-muted mb-3" />
          <h5 class="text-muted">{t('drive.no_shared_items')}</h5>
          <p class="text-muted">{t('drive.no_shared_items_hint')}</p>
        </div>
      ) : (
        <div class="table-responsive">
          <table class="table table-hover">
            <thead class="d-none d-sm-table-header-group">
              <tr>
                <th>{t('drive.file')}</th>
                <th class="d-none d-md-table-cell">{t('drive.shared_by')}</th>
                <th>{t('drive.shared_at')}</th>
                <th class="d-none d-lg-table-cell">{t('drive.share_type')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {shares.map((share) => (
                <tr key={share.id}>
                  <td>
                    <div class="d-flex align-items-center">
                      <i class={`bi ${share.icon_class || 'bi-file-earmark'} fs-5 me-2`} />
                      <div>
                        <div class="text-wrap text-break">
                          {share.kind === 'file' ? (
                            <a
                              href={share.lightbox_url || '#'}
                              class="text-decoration-none text-dark fw-medium"
                              {...lightboxProps(share.lightbox)}
                            >
                              {share.name}
                            </a>
                          ) : (
                            <a href={share.item_url} class="text-decoration-none text-dark fw-medium">
                              {share.name}
                            </a>
                          )}
                        </div>
                        <small class="text-muted d-block d-sm-none">
                          {t('drive.shared_by')}: {share.shared_by}
                          <br />
                          {formatDate(share.shared_at)}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td class="d-none d-md-table-cell">{share.shared_by}</td>
                  <td class="d-none d-sm-table-cell">{formatDate(share.shared_at)}</td>
                  <td class="d-none d-lg-table-cell">
                    {share.public ? (
                      <span class="badge bg-primary">
                        <i class="bi bi-globe me-1" />
                        {t('drive.public')}
                      </span>
                    ) : (
                      <span class="badge bg-success">
                        <i class="bi bi-people me-1" />
                        {t('drive.internal')}
                      </span>
                    )}
                  </td>
                  <td>
                    <div class="btn-group btn-group-sm" role="group">
                      {share.kind === 'file' ? (
                        <>
                          <a
                            href={share.lightbox_url || '#'}
                            class="btn btn-outline-primary"
                            title={t('drive.view')}
                            {...lightboxProps(share.lightbox)}
                          >
                            <i class="bi bi-eye" />
                          </a>
                          <a
                            href={share.download_url}
                            class="btn btn-outline-secondary"
                            title={t('drive.download')}
                          >
                            <i class="bi bi-download" />
                          </a>
                        </>
                      ) : (
                        <a
                          href={share.item_url}
                          class="btn btn-outline-primary"
                          title={t('drive.open_folder')}
                        >
                          <i class="bi bi-folder-open" />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    </DriveSideChrome>
  )
}
